// Fundamentals Dimension — feature gate (pure logic).
//
// Adds a FUNDAMENTAL strategy dimension so Tradeatory can build/validate strategies
// on point-in-time fundamentals (Sharadar SF1) the SAME way it does technical ones.
// In development → DEFAULTS OFF. Two disable controls:
//   • ENV kill (VITE_FUNDAMENTALS_DIMENSION=off) — SUPREME; always forces OFF.
//   • In-app ADMIN toggle (server-persisted; set via admin-ops, read via the
//     fundamentalsFlagService). Can only ENABLE when the env kill allows it.
//
// Enablement precedence (most-authoritative first):
//   1. local kill  (localStorage 'fundamentals_dimension_kill'=1)   → OFF (dev/emergency)
//   2. ENV off     (VITE_FUNDAMENTALS_DIMENSION=off)                 → OFF  ← SUPREME operator kill
//   3. ENV on      (VITE_FUNDAMENTALS_DIMENSION=on)                  → ON
//   4. ADMIN flag  (server-persisted, is_admin-gated to change)     → ON   ← only reached when env allows
//   5. local opt-in(localStorage 'fundamentals_dimension_optin'=1)  → ON  (dev/QA)
//   6. default                                                      → OFF
//
// The admin flag is FAIL-CLOSED: it only enables when it is loaded AND true; an
// unloaded/unreadable admin flag never turns the feature on.

import { publicEnv } from './clientEnv';

export type FundamentalsGateReason =
  | 'local_kill' | 'disabled_by_admin_env' | 'enabled_by_admin_env'
  | 'enabled_by_admin' | 'local_optin' | 'default_off';
export type FundamentalsDimensionGate = { enabled: boolean; reason: FundamentalsGateReason };

export const FUNDAMENTALS_DIMENSION_OPTIN_KEY = 'fundamentals_dimension_optin';
export const FUNDAMENTALS_DIMENSION_KILL_KEY = 'fundamentals_dimension_kill';

/** Cached server/admin flag state. Only ENABLES when loaded && enabled (fail-closed). */
export interface FundamentalsAdminFlagState { loaded: boolean; enabled: boolean }
let adminState: FundamentalsAdminFlagState = { loaded: false, enabled: false };

/** The fundamentalsFlagService calls this after fetching the persisted admin flag. */
export function setFundamentalsAdminFlag(state: FundamentalsAdminFlagState): void {
  adminState = { loaded: !!state.loaded, enabled: !!state.enabled };
}
export function getFundamentalsAdminFlag(): FundamentalsAdminFlagState {
  return adminState;
}

function ls(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; }
}

/**
 * Pure gate. `opts.admin` overrides the module-cached admin state (used by tests and
 * by callers that already hold the fetched state); otherwise the cached state is read.
 */
export function fundamentalsDimensionGate(opts?: { admin?: FundamentalsAdminFlagState }): FundamentalsDimensionGate {
  const on = (reason: FundamentalsGateReason): FundamentalsDimensionGate => ({ enabled: true, reason });
  const off = (reason: FundamentalsGateReason): FundamentalsDimensionGate => ({ enabled: false, reason });

  // 1. per-browser local kill (dev/emergency).
  if (ls(FUNDAMENTALS_DIMENSION_KILL_KEY) === '1') return off('local_kill');
  // 2/3. ENV is SUPREME. 'off' forces OFF even if the admin flag is ON.
  const e = (publicEnv('VITE_FUNDAMENTALS_DIMENSION') ?? '').toLowerCase();
  if (e === 'off') return off('disabled_by_admin_env');
  if (e === 'on') return on('enabled_by_admin_env');
  // 4. ADMIN / server flag — only reached because env did not force OFF. Fail-closed.
  const admin = opts?.admin ?? adminState;
  if (admin.loaded && admin.enabled) return on('enabled_by_admin');
  // 5. local opt-in (dev/QA self-serve).
  if (ls(FUNDAMENTALS_DIMENSION_OPTIN_KEY) === '1') return on('local_optin');
  // 6. default.
  return off('default_off');
}

// Dev/QA convenience: flip the per-browser opt-in.
export function engageFundamentalsDimension(on: boolean): void {
  try {
    if (on) localStorage.setItem(FUNDAMENTALS_DIMENSION_OPTIN_KEY, '1');
    else localStorage.removeItem(FUNDAMENTALS_DIMENSION_OPTIN_KEY);
  } catch { /* ignore */ }
}
